import { generateClient } from 'aws-amplify/data';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

/**
 * @type {import('aws-amplify/data').Client<import('../../../amplify/data/resource').Schema>}
 */
const client = generateClient();

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Notification.listNotificationByUserIdAndCreatedAt({
        userId,
        sortDirection: 'ASC',
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const fetchUnreadNotifications = createAsyncThunk(
  'notifications/fetchUnreadNotifications',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Notification.listNotificationByUserIdAndCreatedAt({
        userId,
        filter: { isUnRead: { eq: true } },
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const fetchUnreadNotificationsLength = createAsyncThunk(
  'notifications/fetchUnreadNotificationsLength',
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Notification.listNotificationByUserIdAndCreatedAt({
        userId,
        filter: { isUnRead: { eq: true } },
        selectionSet: ['id'],
      });

      return response.data.length;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const readNotification = createAsyncThunk(
  'notifications/readNotification',
  async ({ notificationId }, { rejectWithValue }) => {
    try {
      const response = await client.models.Notification.update({
        id: notificationId,
        isUnRead: false,
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

export const createNotification = createAsyncThunk(
  'notifications/createNotification',
  async ({ reportId, userId, type, category, title }, { rejectWithValue }) => {
    try {
      const response = await client.models.Notification.create({
        isUnRead: true,
        type,
        category,
        title,
        reportId,
        userId,
      });

      return response.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error.message || 'An error occurred while fetching messages.');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  notifications: [],
  unReadNotifications: 0,
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(readNotification.pending, (state, action) => {
        const { notificationId } = action.meta.arg;

        const notification = state.notifications.find((n) => n.id === notificationId);
        if (notification && notification.isUnRead) {
          notification.isUnRead = false;
          state.unReadNotifications = Math.max(0, state.unReadNotifications - 1);
        }

        state.loading = true;
        state.error = null;
      })
      .addCase(readNotification.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(readNotification.rejected, (state, action) => {
        const { notificationId } = action.meta.arg;

        const notification = state.notifications.find((n) => n.id === notificationId);
        if (notification) {
          notification.isUnRead = true;
          state.unReadNotifications += 1;
        }

        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.notifications = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUnreadNotificationsLength.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnreadNotificationsLength.fulfilled, (state, action) => {
        state.loading = false;
        state.unReadNotifications = action.payload;
      })
      .addCase(fetchUnreadNotificationsLength.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default notificationsSlice.reducer;
